/* eslint-disable no-underscore-dangle */
/* eslint-disable vue/prop-name-casing */
import {
  defineComponent, ref, reactive, computed, watch, cloneVNode, VNode, PropType, provide, inject,
} from 'vue'
import type { Options as OffsetOptions } from '@floating-ui/core/src/middleware/offset'
import Option from './Option'
import { Popover } from '../Popover'
import { Grid } from '../Grid'
import { dropdownProvision, DropdownProvision } from './provision'

import { OptionContent, OptionDivider } from './interface'
import { Placement, Trigger, DropdownSize } from '../../enums'

export default defineComponent({
  inheritAttrs: false,
  props: {
    /**
     * Dropdown 目标元素
     */
    target: {
      type: Object as PropType<HTMLElement>,
      default: undefined,
    },
    /**
     * Dropdown 展示位置
     */
    placement: {
      type: String as PropType<Placement>,
      default: 'bottom-start',
    },
    /**
     * Dropdown 与目标元素的距离
     */
    offset: {
      type: [Number, Object, Function] as PropType<OffsetOptions>,
      default: 4,
    },
    /**
     * Dropdown 触发方式
     */
    trigger: {
      type: String as PropType<Trigger>,
      default: 'hover',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Function as PropType<() => boolean>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Dropdown 大小
     */
    size: {
      type: String as PropType<DropdownSize>,
      default: 'default',
    },
    options: {
      type: Array as PropType<(OptionContent | OptionDivider | string)[]>,
      default: () => ([]),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    revertable: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number, Boolean, Array] as PropType<(string | number | boolean | (string | number | boolean)[])>,
      default: undefined,
    },
    mark: {
      type: String,
      default: undefined,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: [String, Number],
      default: undefined,
    },
    maxHeight: {
      type: [String, Number],
      default: undefined,
    },
    individual: {
      type: Boolean,
      default: false,
    },
    virtualize: {
      type: Boolean,
      default: false,
    },
    virtualizeOptions: {
      type: Object as PropType<{ optionHeight?: number; optionGap?: number; renderLimit?: number; preCreate?: boolean }>,
      default: undefined,
    },
    /** @internal */
    _root: {
      type: Boolean,
      default: true,
    },
  },
  emits: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    'update:modelValue'(modelValue: string | number | boolean | (string | number | boolean)[] | undefined) {
      return true
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    'change'(modelValue: string | number | boolean | (string | number | boolean)[] | undefined, active: OptionContent[]) {
      return true
    },
    click(e: MouseEvent) {
      return e !== undefined
    },
    'click:dropdown'(e: MouseEvent) {
      return e !== undefined
    },
    'click:target'(e: MouseEvent) {
      return e !== undefined
    },
    'click:outside'(e: MouseEvent) {
      return e !== undefined
    },
    mousedown(e: MouseEvent) {
      return e !== undefined
    },
    'mousedown:dropdown'(e: MouseEvent) {
      return e !== undefined
    },
    'mousedown:target'(e: MouseEvent) {
      return e !== undefined
    },
    'mousedown:outside'(e: MouseEvent) {
      return e !== undefined
    },
    mouseenter(e: MouseEvent) {
      return e !== undefined
    },
    mouseleave(e: MouseEvent) {
      return e !== undefined
    },
    mouseup(e: MouseEvent) {
      return e !== undefined
    },
    'mouseup:dropdown'(e: MouseEvent) {
      return e !== undefined
    },
    'mouseup:target'(e: MouseEvent) {
      return e !== undefined
    },
    'mouseup:outside'(e: MouseEvent) {
      return e !== undefined
    },
  },
  setup(props, { slots, attrs, emit }) {
    const dropdown = inject<DropdownProvision | undefined>(dropdownProvision, undefined)

    const modelValue = props._root || !dropdown
      ? ref(props.modelValue)
      : dropdown?.modelValue
    const activeOptions = props._root || !dropdown
      ? ref<Record<string, OptionContent>>({})
      : dropdown?.activeOptions
    const optionsDescription = props._root || !dropdown
      ? reactive<Record<string, Record<string, { option: OptionContent; active: boolean; subActive: boolean }>>>({})
      : dropdown?.optionsDescription

    if (props._root) {
      watch(
        modelValue,
        v => emit('update:modelValue', v),
      )

      watch(
        () => props.modelValue,
        v => {
          modelValue.value = v
        },
      )

      watch(
        activeOptions,
        v => {
          let redundantUpdate = false

          // eslint-disable-next-line no-nested-ternary
          const values = Array.isArray(modelValue.value)
            ? modelValue.value
            : modelValue.value !== undefined
              ? [modelValue.value]
              : []
          const temp = Object.keys(v)
            .reduce(
              (a, c) => {
                if (values.includes(v[c].value)) {
                  return {
                    ...a,
                    [c]: v[c],
                  }
                }

                redundantUpdate = true

                return a
              },
              {},
            )

          if (redundantUpdate) {
            activeOptions.value = temp
          } else {
            emit('change', modelValue.value, Object.values(v))
          }
        },
      )
    }

    const rawOptions = computed(
      () => [...(slots.options?.() || []), ...props.options],
    )

    const virtualize = computed(
      () => !props.dense && props.virtualize,
    )
    const virtualizeBefore = ref(1)
    const virtualizeOptions = computed(
      () => {
        const optionHeight = props.virtualizeOptions?.optionHeight ?? 32
        const optionGap = props.virtualizeOptions?.optionGap ?? 8
        const renderLimit = props.virtualizeOptions?.renderLimit ?? 20
        const preCreate = !!props.virtualizeOptions?.preCreate
        const renderStep = optionHeight + optionGap

        return {
          optionHeight,
          optionGap,
          renderLimit,
          preCreate,
          renderStep,
        }
      },
    )
    const virtualizeAfter = computed(
      () => (virtualizeBefore.value - 1) + virtualizeOptions.value.renderLimit,
    )

    const optionsContent = ref<{ $el: HTMLElement }>()
    const optionsWrapperStyle = computed(
      () => ({
        paddingTop: `${virtualizeOptions.value.renderStep * (virtualizeBefore.value - 1)}px`,
        height: `calc(${
          (rawOptions.value.length - 1) * virtualizeOptions.value.renderStep + virtualizeOptions.value.optionHeight
        }px${(
          optionsContent.value?.$el
            ? ['paddingTop', 'paddingBottom', 'marginTop', 'marginBottom']
              .map(
                x => ` + ${getComputedStyle(optionsContent.value.$el)[x]}`,
              )
              .join('')
            : ''
        )}`,
      }),
    )

    const handlingScroll = ref<boolean>(false)
    function handleScroll(e: Event) {
      if (!handlingScroll.value) {
        handlingScroll.value = true

        requestAnimationFrame(
          () => {
            const temp = (Math.floor((e.target as HTMLElement).scrollTop / virtualizeOptions.value.renderStep) - Math.floor(virtualizeOptions.value.renderLimit / 2))

            virtualizeBefore.value = temp > 0 ? temp + 1 : 1
            handlingScroll.value = false
          },
        )
      }
    }

    provide<DropdownProvision>(
      dropdownProvision,
      {
        trigger: computed(
          () => (
            props.trigger !== 'manual'
              ? props.trigger
              : 'hover'
          ),
        ),
        visible: computed(
          () => props.visible,
        ),
        size: computed(
          () => props.size,
        ),
        multiple: computed(
          () => props.multiple,
        ),
        revertable: computed(
          () => props.revertable,
        ),
        modelValue,
        activeOptions,
        optionsDescription,
        mark: computed(
          () => props.mark,
        ),
        maxWidth: computed(
          () => props.maxWidth,
        ),
        maxHeight: computed(
          () => props.maxHeight,
        ),
        dense: computed(
          () => props.dense,
        ),
        virtualize,
        virtualizeOptions: computed(
          () => props.virtualizeOptions,
        ),
        optionSlot: computed(
          () => slots.option,
        ),
        optionPrefixSlot: computed(
          () => slots.optionPrefix,
        ),
        optionContentSlot: computed(
          () => slots.optionContent,
        ),
        optionSuffixSlot: computed(
          () => slots.optionSuffix,
        ),
        optionVirtualize: computed(
          () => (
            virtualize.value && {
              virtualizeBefore: virtualizeBefore.value,
              virtualizeAfter: virtualizeAfter.value,
            }
          ),
        ),
      },
    )

    function isVnode(o: OptionContent | OptionDivider | VNode): o is VNode {
      return 'props' in o
    }

    function isDivider(o: OptionContent | OptionDivider): o is OptionDivider {
      return 'divider' in o
    }

    return () => {
      const options = rawOptions.value
        .map(
          (o: OptionContent | OptionDivider | string | VNode, i) => {
            const index = i + 1

            if (
              virtualize.value
              && !virtualizeOptions.value.preCreate
              && (index < virtualizeBefore.value || index > virtualizeAfter.value)
            ) {
              return null
            }

            const bindings = {
              _index: index,
            }

            if (typeof o === 'string') {
              return <Option
                {...bindings}
                key={o}
                name={o}
                label={o}
                value={o}
              />
            }

            if (isVnode(o)) {
              return cloneVNode(
                o,
                {
                  ...bindings,
                  key: `${o.props?.label || o.props?.name}-${o.props?.value}-${o.props?.id}`,
                },
              )
            }

            if (isDivider(o)) {
              return <Option
                {...bindings}
                divider={o.divider}
              />
            }

            return <Option
              {...bindings}
              key={`${o.label || o.name}-${o.value}-${o.id}`}
              disabled={o.disabled}
              icon={o.icon}
              name={o.name}
              label={o.label}
              value={o.value}
              tooltip={o.tooltip}
              mark={o.mark}
              placement={o.placement}
              offset={o.offset}
              trigger={o.trigger}
              visible={o.visible}
              size={o.size}
              children={o.children}
              maxDropdownWidth={o.maxDropdownWidth}
              maxDropdownHeight={o.maxDropdownHeight}
              dropdownClass={o.dropdownClass}
              dropdownStyle={o.dropdownStyle}
              id={o.id}
              virtualize={o.virtualize}
              virtualizeOptions={o.virtualizeOptions}
              extra={o.extra}
              {
                ...{
                  onClick: o.onClick,
                  onMousedown: o.onMousedown,
                  onMouseenter: o.onMouseenter,
                  onMouseleave: o.onMouseleave,
                  onMouseup: o.onMouseup,
                }
              }
            />
          },
        )

      const bindings = {
        class: [
          'd-dropdown',
          props.maxWidth !== undefined
            ? `--size-min-width-${props.size}`
            : `--size-width-${props.size}`,
          props.individual && 'd-dropdown-individual',
          attrs.class,
        ],
        style: [
          {
            ...props.maxWidth !== undefined
            && {
              maxWidth: typeof props.maxWidth === 'number'
                ? `${props.maxWidth}px`
                : props.maxWidth,
            },
            ...props.maxHeight !== undefined
            && {
              maxHeight: typeof props.maxHeight === 'number'
                ? `${props.maxHeight}px`
                : props.maxHeight,
            },
          },
          attrs.style,
        ],
      }

      const content = <>
        {slots.stickTop?.()}
        <div
          class={'d-dropdown-content'}
          {
            ...virtualize.value && {
              onScroll: handleScroll,
            }
          }
        >
          {slots.top?.()}
          {slots.content?.(props)}
          {
            props.dense
              ? <div
                class={
                  [
                    'd-options-dense-wrapper',
                    slots.content && 'd-options-hidden',
                  ]
                }
              >
                {options}
              </div>
              : <div
                class={'d-options-wrapper'}
                style={virtualize.value && optionsWrapperStyle.value}>
                {
                // in order to update modelValue accordingly in case of initial content with options
                  options.length > 0 && <Grid
                    ref={optionsContent}
                    class={
                      [
                        'd-options',
                        slots.content && 'd-options-hidden',
                      ]
                    }
                  >
                    {options}
                  </Grid>
                }
              </div>
          }
          {slots.bottom?.()}
        </div>
        {slots.stickBottom?.()}
      </>

      return props.individual
        ? (
          (slots.content || options.length > 0) && <div
            {...bindings}
            {
              ...{
                onClick: (e: MouseEvent) => emit('click', e),
                onMousedown: (e: MouseEvent) => emit('mousedown', e),
                onMouseenter: (e: MouseEvent) => emit('mouseenter', e),
                onMouseleave: (e: MouseEvent) => emit('mouseleave', e),
                onMouseup: (e: MouseEvent) => emit('mouseup', e),
              }
            }
          >
            {content}
          </div>
        )
        : <Popover
          target={props.target}
          placement={props.placement}
          offset={props.offset}
          trigger={props.trigger}
          visible={props.visible}
          validate={props.validate}
          disabled={props.disabled}
          prepend={true}
          {...bindings}
          {
            ...{
              onClick: (e: MouseEvent) => emit('click', e),
              'onClick:popover': (e: MouseEvent) => emit('click:dropdown', e),
              'onClick:target': (e: MouseEvent) => emit('click:target', e),
              'onClick:outside': (e: MouseEvent) => emit('click:outside', e),
              onMousedown: (e: MouseEvent) => emit('mousedown', e),
              'onMousedown:popover': (e: MouseEvent) => emit('mousedown:dropdown', e),
              'onMousedown:target': (e: MouseEvent) => emit('mousedown:target', e),
              'onMousedown:outside': (e: MouseEvent) => emit('mousedown:outside', e),
              onMouseup: (e: MouseEvent) => emit('mouseup', e),
              'onMouseup:popover': (e: MouseEvent) => emit('mouseup:dropdown', e),
              'onMouseup:target': (e: MouseEvent) => emit('mouseup:target', e),
              'onMouseup:outside': (e: MouseEvent) => emit('mouseup:outside', e),
            }
          }
          v-slots={{
            ...(slots.content || options.length > 0) && {
              content: () => content,
            },
            default: slots.default,
          }}
        />
    }
  },
})
